import { components } from "@/api-schema";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { LocationDisplay } from "@/components/data-table/predefined/_helpers/relocation-order-location-display";
import { DataTableRow } from "@/components/data-table/types";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

type RelocationOrder = components["schemas"]["RelocationOrder"];

interface VehicleInfoCellProps {
  row: DataTableRow<RelocationOrder>;
}

export function VehicleInfoCell({ row }: VehicleInfoCellProps) {
  const { t } = useTranslation(["fit", "common"]);
  const vehicle = row.original.vehicle;
  const order = row.original;
  const brand = vehicle?.modelGeneration?.modelFamily?.brand;
  const brandName = brand?.name;
  const modelGenerationName = vehicle?.modelGeneration?.name;

  return (
    <div className="flex w-full items-start gap-4 py-2">
      <div className="flex w-full min-w-0 flex-col gap-2 text-left">
        <div className="flex items-center gap-2 truncate text-base font-semibold">
          {brand && <BrandLogo brand={brand} variant={BrandLogoVariant.COMPACT} />}
          {(brandName || modelGenerationName) && (
            <div className="truncate">{[brandName, modelGenerationName].filter(Boolean).join(" ")}</div>
          )}
        </div>
        <div className="text-muted-foreground flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">{t("fit:VIN")}:</span>
            <span className="font-mono text-xs">{vehicle.vin}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{t("common:Color")}:</span>
            <span>{vehicle.color}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{t("fit:Destination")}:</span>
            <LocationDisplay
              branch={order.destinationBranch}
              address={order.destinationAddress}
              className="text-foreground font-medium"
            />
          </div>
        </div>
        {order.resolveNote && (
          <Card className="border-warning bg-warning/10 dark:border-warning dark:bg-warning/20 text-warning dark:text-warning mt-2 w-full p-1">
            <CardContent className="px-3 py-2">
              <div className="flex items-start gap-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <CardTitle className="text-xs font-semibold">{t("fit:Note")}</CardTitle>
                  <p className="text-xs wrap-break-word whitespace-pre-wrap">{order.resolveNote}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
